import * as React from "react";
import {createContext, useEffect} from "react";
import {useSelector} from "react-redux";
import {has} from "lodash";
import {ArticleEntity} from "../../contract/article";
import useCurrentRoute from "../../hooks/useCurrentRoute";
import useMedia from "../../hooks/useMedia";
import {RootState} from "../../redux/store";
import {ArticleContainer} from "../Containers";
import Link from "../Link";
import useContentApi from "../../hooks/useContentApi";
import {INITIAL_ROUTE} from "../../hooks/useRoutes";

type ArticleEntityContext = ArticleEntity & {
  loading?: boolean;
  visible?: boolean;
}

export const ARTICLE_LOADING: ArticleEntityContext = {
  id: null,
  slug: null,
  publishedTime: null,
  updatedTime: null,
  publication_status: null,

  // content
  title: null,
  titleShort: null,
  introduction: null,
  introductionShort: null,
  streamer: null,
  streamerAttribution: null,
  callout: null,
  calloutPosition: null,
  featuredImage: null,
  imageCredit: null,

  // meta
  topics: [],

  // state
  loading: true,
  visible: false,
};

export const ArticleContext = createContext<{
  content: ArticleEntityContext,
}>({
  content: ARTICLE_LOADING
});

interface Props {
  content: ArticleEntity;
  direction?: string;
  link?: boolean;
  priority?: number;
  style?: {[key: string]: number|string}
  children: any;
}

const ArticleCard: React.FC<Props> = ({
  content,
  direction,
  link = true,
  priority = 0,
  children,
  style,
  ...props
}): React.ReactElement => {
  const contentApi = useContentApi();
  const pathname = useCurrentRoute();
  const {onMobile} = useMedia();
  const {articles, articles_visible, base} = useSelector((state: RootState) => {
    const articles = Object.values(state.entities.articles.byId);
    const {config, routes} = state.application;
    const {framed, prefix} = config;
    const base = `/${prefix}${framed ? '/framed' : ''}`;

    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {articles_visible} = route;

    return {
      articles,
      articles_visible: (articles_visible || []),
      base,
    }
  });

  const slug: string = content ? content.slug : null;
  const visible: boolean = content ? (articles_visible || []).find(id => id === content?.id) : false;
  const getLink: (slug: string) => string = (slug) => slug && `${base}/${slug}`;

  const anchorStyles = {
    alignItems: onMobile && direction === 'row' ? 'center' : 'unset',
    cursor: visible ? 'pointer' : 'default',
    ...style
  };

  useEffect(() => {
    if (!slug || !contentApi) return;
    let active = true;

    const load = async (): Promise<ArticleEntity> => {
      try {
        const article = (articles || []).find((node: ArticleEntity) => String(node.slug) === slug);

        if (article && priority > 0) {
          const result = await contentApi.queue.add(
            () => contentApi.getArticleContent(article.slug),
            {id: article.id, priority}
          );

          if (!active || !result) return;
          return Array.isArray(result) ? result[0] : result;
        }
      } catch (e) {
        console.error(e.toString());
        return null;
      }
    }

    load().then((result: ArticleEntity) => {
      if (!result) return; // cancelled request

      if (!result.id) throw new Error(`Malformed response for ${slug}`);
    });

    return (): void => {
      active = false;

      // cancel outstanding subscriptions
      contentApi.cancel(slug);
    }
  }, [slug, priority]); // eslint-disable-line react-hooks/exhaustive-deps

  const articleContent: React.ReactElement = (
    <ArticleContainer
      direction={direction}
      {...props}
      style={(link && slug) ? {} : anchorStyles}
    >
      {children}
    </ArticleContainer>
  );

  return (link && slug) ? (
    <Link
      disabled={!visible}
      to={getLink(slug)}
      style={anchorStyles}
      className={"theme--focus-primary-color theme--thumbnail-border-radius"}
    >
      {articleContent}
    </Link>
  ) : articleContent;
}

const ArticleWrapper: React.FC<Props> = ({
  content,
  children,
  ...props
}): React.ReactElement => (
  <ArticleContext.Provider value={{content}}>
    <ArticleCard content={content} {...props}>{children}</ArticleCard>
  </ArticleContext.Provider>
);

export default ArticleWrapper;
